import { useEffect, useRef, useState } from 'react'
import { useUrlMetadataPrefill } from './useUrlMetadataPrefill'
import type { CheckInterval } from '../lib/time'
import type { Link } from '../api/types'

export function useLinkDialogForm(
  open: boolean,
  link: Link | null,
  initialUrl?: string,
  defaultFolderId?: number | null,
) {
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [pinned, setPinned] = useState(false)
  const [folderId, setFolderId] = useState<number | null>(null)
  const [checkInterval, setCheckInterval] = useState<CheckInterval | null>(null)
  const [autofillFailed, setAutofillFailed] = useState(false)
  const [autofillPending, setAutofillPending] = useState(false)
  const [ogPreview, setOgPreview] = useState<string | undefined>(undefined)
  const [saveError, setSaveError] = useState<string | null>(null)
  // The optimistic-lock baseline: the row as last known on the server.
  // Screenshot capture and a partial save both advance updated_at behind
  // the dialog's back; without refreshing this, the next Save 409s against
  // the dialog's own earlier write.
  const [baseline, setBaseline] = useState<Link | null>(link)
  const urlInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setBaseline(link)
    setUrl(link?.url ?? initialUrl ?? '')
    setTitle(link?.title ?? '')
    setDescription(link?.description ?? '')
    setPinned(link?.pinned ?? false)
    // The context default is CREATE-mode only. An existing link's folder_id
    // is authoritative even when null ("lives on Home") — falling back to
    // defaultFolderId there silently moved an ungrouped link into whatever
    // folder was open on edit (FolderDialog's parentId rule, AppShell).
    setFolderId(link ? (link.folder_id ?? null) : (defaultFolderId ?? null))
    setCheckInterval(link?.check_interval ?? null)
    setAutofillFailed(false)
    setAutofillPending(false)
    setOgPreview(undefined)
    setSaveError(null)
  }, [open, link, initialUrl, defaultFolderId])

  useUrlMetadataPrefill({
    url,
    skip: !!link || !open,
    setTitle,
    setDescription,
    setAutofillFailed,
    setAutofillPending,
    setOgPreview,
  })

  const values = {
    url,
    title,
    description,
    pinned,
    folderId,
    checkInterval,
  }
  return {
    ...values,
    baseline,
    refreshBaseline: setBaseline,
    autofillFailed,
    autofillPending,
    ogPreview,
    saveError,
    urlInputRef,
    setUrl,
    setTitle,
    setDescription,
    setPinned,
    setFolderId,
    setCheckInterval,
    setSaveError,
  }
}
